import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO } from '../../components/SEO';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';

const getTranslation = (t: (k: string) => string, key: string, fallback: string): string => {
  const translation = t(key);
  return translation === key ? fallback : translation;
};

export function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // The recovery endpoint isn't wired yet; we set the submitted state so the user
    // gets a clear next step instead of silent retries on /login.
    setSubmitted(true);
  };

  return (
    <>
      <SEO
        title="Reset password | InterviewR"
        description="Recover access to your InterviewR account."
        url={typeof window !== 'undefined' ? window.location.href : 'https://interview-r.com/auth/forgot-password'}
        noindex
      />
      <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-stitch-canvas px-4 py-16 sm:px-6 lg:px-8">
        <div className="relative z-[1] w-full max-w-md">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-stitch-xl bg-stitch-primary-soft text-stitch-primary shadow-stitch-card">
              <span className="material-symbols-outlined text-3xl">key</span>
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-stitch-fg sm:text-3xl">
              {getTranslation(t, 'auth.forgotTitle', 'Reset your password')}
            </h1>
            <p className="mt-2 text-sm text-stitch-muted">
              {getTranslation(
                t,
                'auth.forgotSubtitle',
                "Enter your email and we'll send you a link to choose a new password.",
              )}
            </p>
          </div>

          <div className="rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-modal sm:p-8">
            {submitted ? (
              <div className="space-y-4 text-center">
                <span className="material-symbols-outlined text-4xl text-stitch-success">mark_email_read</span>
                <h2 className="text-lg font-semibold text-stitch-fg">
                  {getTranslation(t, 'auth.forgotSent', 'Check your inbox')}
                </h2>
                <p className="text-sm text-stitch-muted">
                  {getTranslation(
                    t,
                    'auth.forgotSentDesc',
                    "If an account exists for that email, we've sent reset instructions. The link expires in 30 minutes.",
                  )}
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1 text-sm font-semibold text-stitch-primary hover:text-stitch-primary-hover"
                >
                  <span className="material-symbols-outlined text-base">arrow_back</span>
                  {getTranslation(t, 'auth.backToLogin', 'Back to sign in')}
                </Link>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <Input
                  id="forgot-email"
                  type="email"
                  required
                  label={getTranslation(t, 'auth.email', 'Email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
                <Button type="submit" variant="primary" size="lg" className="w-full" disabled={!email}>
                  {getTranslation(t, 'auth.forgotCta', 'Send reset link')}
                </Button>
                <p className="text-center text-sm text-stitch-muted">
                  <Link to="/login" className="font-semibold text-stitch-primary hover:text-stitch-primary-hover">
                    {getTranslation(t, 'auth.backToLogin', 'Back to sign in')}
                  </Link>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
